import { AlertEnum, ButtonVariantEnum, LayoutEnum, SituacaoEleicaoEnum } from '../enums';
import { BuscaEleitores, Button, DefaultContent, EleitoresList } from '../components';
import { EleicaoResponseType, EleitorResponseType, EleitoresGerenciamentoViewType, GrupoEleitoresResponseType } from '../types';
import { FunctionComponent, useEffect, useState } from 'react';
import { useAlert, useAuthAxios, useErrorHandler, useErrorStatus, useLayout, useModal } from '../hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiHelper } from '../helpers';
import { Formik } from 'formik';
import { Spinner } from '../utils';
import { StatusCodes } from 'http-status-codes';
import { useTranslation } from 'react-i18next';

const EleitoresGerenciamentoView: FunctionComponent<EleitoresGerenciamentoViewType> = ({ tipo }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const axios = useAuthAxios();
  const { createAlert } = useAlert();
  const { confirmModal } = useModal();
  const { setErrorStatus } = useErrorStatus();
  const errorHandler = useErrorHandler();

  const { idEleicao, idGrupoEleitores } = useParams();
  const [eleicao, setEleicao] = useState<EleicaoResponseType>();
  const [loadingEleicao, setLoadingEleicao] = useState(true);
  const [grupoEleitores, setGrupoEleitores] = useState<GrupoEleitoresResponseType>();
  const [loadingGrupoEleitores, setLoadingGrupoEleitores] = useState(true);
  const [eleitoresGrupo, setEleitoresGrupo] = useState<EleitorResponseType[]>();
  const [eleitoresTipo, setEleitoresTipo] = useState<EleitorResponseType[]>();
  const [loadingEleitoresTipo, setLoadingEleitoresTipo] = useState(true);
  const [buscaEleitores, setBuscaEleitores] = useState<EleitorResponseType[] | null>(null);
  const [loadingBuscaEleitores, setLoadingBuscaEleitores] = useState(false);

  const [totalEleitores, setTotalEleitores] = useState(0);
  const [totalEleitoresValidos, setTotalEleitoresValidos] = useState(0);

  const [showBusca, setShowBusca] = useState(false);
  const { setLayout } = useLayout();

  const toggleBusca = (
    <Button
      title={showBusca ? `Esconder Busca` : `Buscar Eleitores`}
      outline={showBusca}
      onClick={(e) => {
        if (!showBusca) {
          handleLimparBusca();
        }
        setShowBusca(!showBusca);
      }}
      className={'text-nowrap'}
    >
      {showBusca ? `Esconder Busca` : `Buscar Eleitores`}
    </Button>
  );

  useEffect(() => {
    if (showBusca) {
      window.location.href = '#busca';
    } else {
      window.location.href = '#';
    }
  }, [showBusca]);

  const handleLimparBusca = () => {
    setBuscaEleitores(null);
  };

  const handleListUpdate = (eleitoresInitials: EleitorResponseType[], checkSaved: boolean) => {
    const initialValues: any = { checkAll: false };
    eleitoresInitials?.forEach((e: EleitorResponseType) => {
      initialValues[e.id] = checkSaved && eleitoresTipo?.find((eleitor) => eleitor.id === e.id) ? true : false;
    });
    return initialValues;
  };

  const handleTotals = (grupoEleitores: GrupoEleitoresResponseType) => {
    const totaisGrupo = grupoEleitores?.totais?.find((t) => t.tipo === tipo.key);
    let totalEleitores = totaisGrupo?.total ?? 0;
    let totalEleitoresValidos = totaisGrupo?.unicos ?? 0;
    if (tipo.equivalencia) {
      const totaisGrupoEquivalente = grupoEleitores?.totais?.find((t) => t.tipo === tipo.equivalencia);
      totalEleitores += totaisGrupoEquivalente?.total ?? 0;
      totalEleitoresValidos += totaisGrupoEquivalente?.unicos ?? 0;
    }
    setTotalEleitores(totalEleitores);
    setTotalEleitoresValidos(totalEleitoresValidos);
  };

  const processBuscaBeforeSubmit = (values: any) => {
    let submissionList = eleitoresGrupo;
    Object.keys(values!).forEach((key) => {
      if (!values[key]) {
        submissionList = submissionList!.filter((e) => e.id !== +key || e.tipo !== tipo.key);
      } else if (
        !submissionList?.find(
          (e: EleitorResponseType) => e.id === +key && (e.tipo === tipo.key || e.tipo === tipo.equivalencia!),
        )
      ) {
        let addEleitor = buscaEleitores!.find((e: EleitorResponseType) => e.id === +key);
        if (addEleitor) {
          submissionList = [...submissionList!, addEleitor!];
        }
      }
    });
    return submissionList;
  };

  const processEleitoresRemocaoBeforeSubmit = (values: any) => {
    let submissionList = eleitoresGrupo;
    Object.keys(values!).forEach((key) => {
      if (values[key]) {
        submissionList = submissionList!.filter((e) => e.id !== +key || (e.tipo !== tipo.key && e.tipo !== tipo.equivalencia!));
      }
    });
    return submissionList;
  };

  const processResponse = (eleitores: EleitorResponseType[]) => {
    const eleitoresFiltradosPorTipo = eleitores.filter((eleitor: EleitorResponseType) => {
      return eleitor.tipo === tipo.key || eleitor.tipo === tipo.equivalencia!;
    });
    if (tipo.equivalencia) {
      eleitoresFiltradosPorTipo.sort((a, b) => a.nome.localeCompare(b.nome));
    }
    setEleitoresGrupo(eleitores);
    setEleitoresTipo(eleitoresFiltradosPorTipo);
    setTotalEleitores(eleitoresFiltradosPorTipo.length);
  };

  useEffect(() => {
    setLayout(LayoutEnum.BASE_LAYOUT);
  }, []);

  useEffect(() => {
    if (idEleicao) {
      if (ApiHelper.isValidIntegerToApi(idEleicao)) {
        axios
          .get(ApiHelper.getUrl.apiSCE.eleicao(+idEleicao))
          .then((response) => {
            const dadosEleicao: EleicaoResponseType = response.data;
            if (dadosEleicao.situacao !== SituacaoEleicaoEnum.NAO_PUBLICADA.key) {
              navigate(`/eleicoes/${idEleicao}`);
            }
            setEleicao(response.data);
          })
          .catch((error) => {
            if (error.response.status === StatusCodes.NOT_FOUND) {
              setErrorStatus(error.response.status);
            } else {
              errorHandler(error, t('erro.recuperar_eleicao'));
            }
          })
          .finally(() => setLoadingEleicao(false));
      } else {
        setTimeout(() => setErrorStatus(StatusCodes.NOT_FOUND));
      }
    }
  }, [idEleicao]);

  useEffect(() => {
    if (idEleicao && idGrupoEleitores) {
      axios
        .get(ApiHelper.getUrl.apiSCE.eleitores(+idEleicao, +idGrupoEleitores))
        .then((response) => {
          processResponse(response.data);
        })
        .catch((error) => {
          if (error.response.status === StatusCodes.NOT_FOUND) {
            setErrorStatus(error.response.status);
          } else {
            errorHandler(error, t('erro.recuperar_eleitores'));
          }
        })
        .finally(() => {
          setLoadingEleitoresTipo(false);
        });
    } else {
      setTimeout(() => setErrorStatus(StatusCodes.NOT_FOUND));
    }
  }, [idEleicao, idGrupoEleitores]);

  useEffect(() => {
    if (idEleicao && idGrupoEleitores) {
      if (ApiHelper.isValidIntegerToApi(idEleicao) && ApiHelper.isValidIntegerToApi(idGrupoEleitores)) {
        axios
          .get(ApiHelper.getUrl.apiSCE.grupoEleitores(+idEleicao, +idGrupoEleitores))
          .then((response) => {
            setGrupoEleitores(response.data);
            handleTotals(response.data);
          })
          .catch((error) => {
            if (error.response.status === StatusCodes.NOT_FOUND) {
              setErrorStatus(error.response.status);
            } else {
              errorHandler(error, t('erro.recuperar_grupo_eleitores'));
            }
          })
          .finally(() => setLoadingGrupoEleitores(false));
      } else {
        setTimeout(() => setErrorStatus(StatusCodes.NOT_FOUND));
      }
    }
  }, [eleitoresTipo]);

  return loadingEleicao || loadingGrupoEleitores ? (
    <Spinner />
  ) : (
    <DefaultContent
      title={t('label.grupo_eleitores')}
      subtitle={eleicao?.titulo!}
      pageAction={
        <div className="d-flex flex-row align-items-center justify-content-end flex-shrink-0 gap-3 gap-md-2 gap-lg-3">
          {totalEleitores > 0 && (
            <div>
              <span className={'fs-md-2 fs-5 '} title={t('texto.total_eleitores_validos')}>
                {totalEleitoresValidos}
              </span>
              <span className="">{` ${t('label.eleitores')} /`}</span>
              <span title={t('texto.total_eleitores')}>{totalEleitores}</span>
            </div>
          )}
          <Button
            title={t('label.voltar')}
            onClick={() => navigate(`/eleicoes/${idEleicao}/grupos-eleitores/alterar`)}
            outline
          />
        </div>
      }
    >
      {loadingEleitoresTipo ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={handleListUpdate(eleitoresTipo!, false)}
          validationSchema={null}
          enableReinitialize
          onSubmit={(values, { setSubmitting, resetForm }) => {
            confirmModal(
              t('confirmacao.remover_eleitores'),
              () => {
                axios(ApiHelper.getUrl.apiSCE.eleitores(eleicao!.id, grupoEleitores!.id), {
                  data: processEleitoresRemocaoBeforeSubmit(values),
                  method: 'PUT',
                })
                  .then((response) => {
                    processResponse(response.data);
                    handleLimparBusca();
                    resetForm();
                    createAlert(t(`sucesso.gerenciar_lista_eleitores`), AlertEnum.SUCCESS);
                  })
                  .catch((error) => errorHandler(error, t(`erro.gerenciar_lista_eleitores`)))
                  .finally(() => setSubmitting(false));
              },
              ButtonVariantEnum.DANGER,
              t('label.remover_eleitores'),
              () => setSubmitting(false),
            );
          }}
        >
          {(props) => (
            <EleitoresList
              formikProps={props}
              eleitoresDisplayList={eleitoresTipo!}
              idEleicao={eleicao!.id}
              cardTitle={t(`label.eleitores_salvos`, { eleitores: t(tipo.descricao) })}
              cardSubtitle={t(grupoEleitores?.denominacao!)}
              cardIcon={tipo.icon}
              cardInfo={t(`texto.eleitores_salvos`, { eleitores: t(tipo.descricao) })}
              emptySearchMsg={t('texto.lista_eleitores_vazia')}
              expandCard={!showBusca}
              isForRemoval
              headerAction={toggleBusca}
              mainActionTitle={t('label.remover')}
              mainActionButtonVariant={ButtonVariantEnum.DANGER}
            />
          )}
        </Formik>
      )}
      {showBusca && (
        <BuscaEleitores
          handleLimparBusca={handleLimparBusca}
          setResultado={setBuscaEleitores}
          setLoading={setLoadingBuscaEleitores}
          tipo={tipo}
        />
      )}
      {!showBusca ? (
        <></>
      ) : loadingBuscaEleitores ? (
        <Spinner />
      ) : (
        buscaEleitores && (
          <Formik
            initialValues={handleListUpdate(buscaEleitores, true)}
            validationSchema={null}
            enableReinitialize
            onSubmit={(values, { setSubmitting, resetForm }) => {
              axios(ApiHelper.getUrl.apiSCE.eleitores(eleicao!.id, grupoEleitores!.id), {
                data: processBuscaBeforeSubmit(values),
                method: 'PUT',
              })
                .then((response) => {
                  processResponse(response.data);
                  handleLimparBusca();
                  resetForm();
                  createAlert(t(`sucesso.gerenciar_lista_eleitores`), AlertEnum.SUCCESS);
                })
                .catch((error) => errorHandler(error, t(`erro.gerenciar_lista_eleitores`)))
                .finally(() => setSubmitting(false));
            }}
          >
            {(props) => (
              <EleitoresList
                formikProps={props}
                eleitoresDisplayList={buscaEleitores!}
                idEleicao={eleicao!.id}
                cardTitle={t('label.eleitores_filtrados')}
                cardSubtitle={t('texto.eleitores_filtrados')}
                emptySearchMsg={t('texto.lista_eleitores_filtrada_vazia')}
                handleLimparBusca={handleLimparBusca}
                mainActionTitle={t(`label.alterar`)}
                mainActionButtonVariant={ButtonVariantEnum.SUCCESS}
                headerAction={toggleBusca}
              />
            )}
          </Formik>
        )
      )}
    </DefaultContent>
  );
};

export default EleitoresGerenciamentoView;
